"use client";

import Script from "next/script";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AjaxPostAnon } from "@/util/Ajax";

const GoogleSignIn = ({
  onAuthenticated,
  canAuthenticate = () => true,
  text = "continue_with",
  showConsentNotice = false,
}) => {
  const buttonRef = useRef(null);
  const onAuthenticatedRef = useRef(onAuthenticated);
  const canAuthenticateRef = useRef(canAuthenticate);
  const [isReady, setIsReady] = useState(false);
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
    canAuthenticateRef.current = canAuthenticate;
  }, [canAuthenticate, onAuthenticated]);

  useEffect(() => {
    if (!isReady || !clientId || !buttonRef.current || !window.google) return;

    const handleCredential = async ({ credential }) => {
      if (!canAuthenticateRef.current()) return;

      const response = await AjaxPostAnon("Authentication/Google", { credential });
      if (response?.data) {
        onAuthenticatedRef.current(response.data);
        return;
      }

      toast.error(
        response?.response?.data?.message || "Google sign-in failed. Please try again."
      );
    };

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredential,
    });
    buttonRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "outline",
      size: "large",
      text,
      shape: "rectangular",
      width: Math.min(buttonRef.current.offsetWidth || 320, 400),
    });
  }, [clientId, isReady, text]);

  if (!clientId) return null;

  return (
    <>
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onLoad={() => setIsReady(true)}
        onReady={() => setIsReady(true)}
      />
      <div className="space-y-4">
        <div ref={buttonRef} className="flex min-h-[44px] w-full justify-center" />
        {showConsentNotice && (
          <p className="text-center text-xs leading-5 text-slate-500 dark:text-slate-400">
            By registering with Google or the form below, you accept our{" "}
            <Link
              href="/termsandconditions"
              target="_blank"
              className="font-medium text-primary-900 hover:underline"
            >
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link
              href="/PrivacyPolicy"
              target="_blank"
              className="font-medium text-primary-900 hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
        )}
        <div className="flex items-center gap-3 text-xs uppercase text-slate-400">
          <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
          <span>or</span>
          <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
    </>
  );
};

export default GoogleSignIn;